/**
 * Pull the code out of a tutor message so it can be shown as code.
 *
 * The tutor writes Markdown. Rendered as plain text, a backtick-quoted snippet
 * reaches the learner as `print("hi")` — and a ten-year-old copies the whole line,
 * backticks included, and gets a syntax error they did not make. Observed in the
 * first real walkthrough, and the tutor could not talk its way out of it: its
 * correction contained backticks too.
 *
 * Deliberately **not** a Markdown renderer. It understands fenced blocks, inline
 * code and `**bold**`, and nothing else — no headings, no links, no lists, no HTML.
 * Everything else stays exactly as the tutor typed it. A full Markdown library would
 * be a much larger surface for the sake of formatting nobody asked for, and would
 * open the door to rendering model output as markup, which is a security question we
 * do not need to have.
 *
 * Emphasis is here for the same reason as code, and arrived the same way — twice. The
 * screenshot verifying the code fix showed `**Write me a while loop**` with its
 * asterisks intact three lines below the snippet just cleaned up; the screenshot
 * verifying *that* fix showed `*third*`. Same defect, one asterisk fewer. Which is a
 * decent argument for handling the whole small family at once rather than one
 * punctuation mark per bug report.
 */

export interface Marks {
  strong?: boolean;
  em?: boolean;
}

export type Segment =
  | ({ kind: "text"; text: string } & Marks)
  | ({ kind: "inline"; text: string } & Marks)
  | { kind: "block"; text: string; language: string; complete: boolean };

const FENCE = "```";

/**
 * Split a message into prose, inline code, and code blocks.
 *
 * Handles a fence that has not closed yet, because messages are parsed while they
 * stream: the opening fence arrives, then the code, then the closing fence some
 * seconds later. Showing the raw ``` in that gap would be worse than showing the
 * block early, so an unterminated fence renders as a block still being written.
 */
export function parseMessage(text: string): Segment[] {
  const segments: Segment[] = [];
  let rest = text;

  while (rest.length > 0) {
    const open = rest.indexOf(FENCE);
    if (open === -1) {
      pushProse(segments, rest);
      break;
    }

    pushProse(segments, rest.slice(0, open));

    const afterOpen = rest.slice(open + FENCE.length);
    // The language tag runs to the end of the line: ```python
    const newline = afterOpen.indexOf("\n");
    const language = (newline === -1 ? afterOpen : afterOpen.slice(0, newline)).trim();
    const body = newline === -1 ? "" : afterOpen.slice(newline + 1);

    const close = body.indexOf(FENCE);
    if (close === -1) {
      // Still streaming. Show what has arrived so far.
      if (body.length > 0 || language.length > 0) {
        segments.push({ kind: "block", text: body, language, complete: false });
      }
      break;
    }

    segments.push({
      kind: "block",
      text: body.slice(0, close).replace(/\n$/, ""),
      language,
      complete: true,
    });
    rest = body.slice(close + FENCE.length);
  }

  return segments;
}

/**
 * Prose: inline code, and bold that may contain inline code.
 *
 * Order is the whole difficulty here, and getting it wrong is not theoretical — the
 * first version handled code first, which split
 *
 *     **Write me a `while` loop from scratch.**
 *
 * so that the opening and closing asterisks landed in different pieces, matched
 * neither, and reached the learner intact. Doing bold first instead breaks the
 * opposite case: in ``try `2 ** 8` and `3 ** 2` `` the asterisks are Python
 * exponentiation and bolding across them mangles both snippets.
 *
 * So code spans are lifted out first and replaced by a sentinel, bold is matched on
 * what remains — where every asterisk is genuinely Markdown — and the code is put
 * back afterwards, carrying whatever emphasis it sat inside.
 */
const SENTINEL = "\u0000";

function pushProse(segments: Segment[], prose: string): void {
  if (prose.length === 0) return;

  const code: string[] = [];
  const hidden = prose.replace(/`([^`\n]+)`/g, (_match, inner: string) => {
    code.push(inner);
    return `${SENTINEL}${code.length - 1}${SENTINEL}`;
  });

  // `**bold**` first in the alternation so a bold run is never read as two italics.
  // Non-greedy, no asterisk and no newline inside: emphasis is one run on one line, so
  // a stray asterisk cannot swallow the rest of the message.
  const emphasis = /\*\*([^*\n]+?)\*\*|\*([^*\n]+?)\*/g;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = emphasis.exec(hidden)) !== null) {
    if (match.index > cursor) expand(segments, hidden.slice(cursor, match.index), code, {});
    if (match[1] !== undefined) expand(segments, match[1], code, { strong: true });
    else expand(segments, match[2]!, code, { em: true });
    cursor = match.index + match[0].length;
  }
  if (cursor < hidden.length) expand(segments, hidden.slice(cursor), code, {});
}

/** Put the code spans back, tagging each piece with the emphasis it sat inside. */
function expand(segments: Segment[], text: string, code: string[], marks: Marks): void {
  if (text.length === 0) return;
  const pattern = new RegExp(`${SENTINEL}(\\d+)${SENTINEL}`, "g");
  let cursor = 0;
  let match: RegExpExecArray | null;

  const push = (segment: Segment) => {
    if (segment.kind !== "block") Object.assign(segment, marks);
    segments.push(segment);
  };

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) push({ kind: "text", text: text.slice(cursor, match.index) });
    push({ kind: "inline", text: code[Number(match[1])] ?? "" });
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) push({ kind: "text", text: text.slice(cursor) });
}

/** Every code block in a message, for the copy button and for tests. */
export function codeBlocks(text: string): string[] {
  return parseMessage(text)
    .filter((s): s is Extract<Segment, { kind: "block" }> => s.kind === "block")
    .map((s) => s.text);
}
